import React, { useEffect, useContext, useState } from "react";
import {
	Button, List, Modal, Select, Space, message
} from "antd";
import { CalendarOutlined, ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { Provider as JotaiProvider, useAtom } from "jotai";
import {
	solicitudAusenciaSeleccionadaAtom,
	solicitudesAtom,
	visibleModalAprobarSolicitudAusenciaAtom,
	visibleModalRechazarSolicitudAusenciaAtom,
} from "./SolicitudesPendientesAprobarStore";
import { ApiResponse } from "enum";
import { SolicitudAusenciaService } from "services";
import moment from "moment";
import { ModalAprobarSolicitudAusencia, ModalRechazarSolicitudAusencia } from "./componentes";
import { SecurityContext } from "context/SecurityContextProvider";
import { useBloqueoMovimientos, AvisoBloqueoMovimientos } from "../../../common/bloqueo-movimientos";

const esDescansoMedicoDeTipo = (tipo) =>
	tipo?.descripcion?.toLowerCase().includes("descanso m") ?? false;

const SolicitudesPendientesAprobarPage = () => {
	return (
		<JotaiProvider>
			<Header />
			<Solicitudes />
			<ModalAprobarSolicitudAusencia />
			<ModalRechazarSolicitudAusencia />
		</JotaiProvider>
	);
};

const Solicitudes = () => {
	const [solicitudes, setSolicitudes] = useAtom(solicitudesAtom);
	const [, setSolicitudSeleccionada] = useAtom(solicitudAusenciaSeleccionadaAtom);
	const [, setVisibleModalAprobarSolicitud] = useAtom(visibleModalAprobarSolicitudAusenciaAtom);
	const [, setVisibleModalRechazarSolicitud] = useAtom(visibleModalRechazarSolicitudAusenciaAtom);
	const { fechaLimite, estaBloqueada } = useBloqueoMovimientos();
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const isMaccaferriDePeru = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20418710757" ? true : false;
	const isMaccaferriConstruction = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20521749378" ? true : false;
	const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ? true : false;
	const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090" ? true : false;

	const [modalDetallesVisible, setModalDetallesVisible] = useState(false);
	const [itemSeleccionado, setItemSeleccionado] = useState(null);
	const [tipoEnfermedad, setTipoEnfermedad] = useState(null);
	const [tiposEnfermedad, setTiposEnfermedad] = useState([]);
	const [loadingGuardar, setLoadingGuardar] = useState(false);

	useEffect(() => {
		const cargarSolicitudes = async () => {
			const response = await SolicitudAusenciaService.listarPorJefeInmediato({ estado: "PENDIENTE" });
			if (response.estado === ApiResponse.EXITO) {
				setSolicitudes(response.data);
			}
		};

		cargarSolicitudes();
	}, []);

	const onClickBtnAprobar = (solicitud) => {
		if (estaBloqueada(solicitud.fechaInicioAusencia)) {
			message.warning("La fecha de inicio de esta solicitud está dentro del periodo bloqueado. Solo puede rechazarla.");
			return;
		}
		if ((isSDF || isSDFEnergia) && esDescansoMedicoDeTipo(solicitud.tipo) && !solicitud.tipoEnfermedad) {
			message.warning("Debe registrar el tipo de enfermedad antes de aprobar. Use 'Ver detalles adicionales' para registrarlo.");
			return;
		}
		setSolicitudSeleccionada(solicitud);
		setVisibleModalAprobarSolicitud(true);
	};

	const onClickBtnRechazar = (solicitud) => {
		setSolicitudSeleccionada(solicitud);
		setVisibleModalRechazarSolicitud(true);
	};

	const onClickVerDetalles = (item) => {
		setItemSeleccionado(item);
		setTipoEnfermedad(item.tipoEnfermedad ?? null);
		setTiposEnfermedad([]);
		if (esDescansoMedicoDeTipo(item.tipo) && (isSDF || isSDFEnergia)) {
			SolicitudAusenciaService.listarTiposEnfermedad()
				.then((response) => {
					if (response.estado === "EXITO") setTiposEnfermedad(response.data);
				})
				.catch(() => {});
		}
		setModalDetallesVisible(true);
	};

	const handleCloseModal = () => {
		setModalDetallesVisible(false);
		setItemSeleccionado(null);
	};

	const onClickGuardarTipoEnfermedad = async () => {
		setLoadingGuardar(true);
		try {
			const response = await SolicitudAusenciaService.guardarTipoEnfermedad({
				solicitudAusenciaId: itemSeleccionado.id,
				tipoEnfermedad: tipoEnfermedad,
			});
			if (response.estado === ApiResponse.EXITO) {
				message.success("Tipo de enfermedad guardado correctamente.");
				setSolicitudes(solicitudes.map((s) =>
					s.id === itemSeleccionado.id ? { ...s, tipoEnfermedad } : s
				));
				setItemSeleccionado({ ...itemSeleccionado, tipoEnfermedad });
			} else {
				message.error(response.mensaje || "No se pudo guardar el tipo de enfermedad.");
			}
		} catch (e) {
			message.error("No se pudo guardar el tipo de enfermedad.");
		}
		setLoadingGuardar(false);
	};

	const shouldShowExpandable = (item) => {
		return (isSDF || isSDFEnergia);
	};

	const getFormatoFecha = (tipo, isSDF, isSDFEnergia) => {
		return (isSDF || isSDFEnergia) && !tipo?.contabilizarHoras
			? "DD/MM/YYYY"
			: "DD/MM/YYYY hh:mm a";
	};

	return (
		<>
			<AvisoBloqueoMovimientos fechaLimite={fechaLimite} accion="aprobar" style={{ margin: 12 }} />
			<List
				itemLayout="horizontal"
				dataSource={solicitudes}
				renderItem={(item) => (
					<List.Item style={{
						padding: "12px",
						display: "block",
					}}>
						<List.Item.Meta
							avatar={<CalendarOutlined style={{ fontSize: "24px" }} />}
							title={item.tipo.descripcion}
							description={
								<div>
									<div>
										{moment(item.fechaInicioAusencia).format(getFormatoFecha(item?.tipo, isSDF, isSDFEnergia))}
										{" - "}
										{moment(item.fechaFinAusencia).format(getFormatoFecha(item?.tipo, isSDF, isSDFEnergia))}
										{" "}({item.diasSolicitados} días)
									</div>
									<div>{item.colaborador.nombreCompleto}</div>
									{(isMaccaferriConstruction || isMaccaferriDePeru) && <div>{item.comentario}</div>}
									<div style={{ marginTop: "5px" }}>
										<Space direction="vertical" style={{ width: "100%" }} size="small">
											<Space>
												<Button
													type="primary"
													shape="round"
													size="small"
													onClick={() => onClickBtnAprobar(item)}
													disabled={estaBloqueada(item.fechaInicioAusencia)}
												>
													Aprobar
												</Button>
												<Button
													type="danger"
													shape="round"
													size="small"
													onClick={() => onClickBtnRechazar(item)}
												>
													Rechazar
												</Button>
											</Space>
											{shouldShowExpandable(item) && (
												<Button
													type="link"
													size="small"
													icon={<InfoCircleOutlined />}
													onClick={() => onClickVerDetalles(item)}
													style={{
														padding: 0,
														fontSize: "13px",
														color: "#1890ff"
													}}
												>
													Ver detalles adicionales
												</Button>
											)}
										</Space>
									</div>
								</div>
							}
						/>
					</List.Item>
				)}
			/>
			{/* Modal con detalles adicionales */}
			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center" }}>
						<InfoCircleOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
						<span>Detalles Adicionales</span>
					</div>
				}
				open={modalDetallesVisible}
				onCancel={handleCloseModal}
				footer={[
					<Button key="close" type="primary" onClick={handleCloseModal}>
						Cerrar
					</Button>
				]}
				width="90%"
				style={{ top: 20 }}
				bodyStyle={{
					maxHeight: "70vh",
					overflowY: "auto",
					padding: "16px"
				}}
			>
				{itemSeleccionado && (
					<div>
						{/* Tipo de Enfermedad — solo DM + SDF/SDFEnergía */}
						{esDescansoMedicoDeTipo(itemSeleccionado?.tipo) && (isSDF || isSDFEnergia) && (
							<div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f6f8fa", borderRadius: 6, border: "1px solid #e8e8e8" }}>
								<span style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13 }}>
									Tipo de Enfermedad
								</span>
								<Select
									placeholder="Seleccione el tipo de enfermedad"
									value={tipoEnfermedad}
									onChange={(value) => setTipoEnfermedad(value)}
									style={{ width: "100%", marginBottom: 8 }}
								>
									{tiposEnfermedad.map((tipo) => (
										<Select.Option key={tipo.id} value={tipo.id}>
											{tipo.descripcion}
										</Select.Option>
									))}
								</Select>
								<Button
									type="primary"
									loading={loadingGuardar}
									disabled={!tipoEnfermedad}
									onClick={onClickGuardarTipoEnfermedad}
									style={{ width: "100%" }}
								>
									Guardar
								</Button>
							</div>
						)}
						{/* Detalles específicos */}
						<Comentario
							registro={itemSeleccionado}
							isSDF={isSDF}
							isSDFEnergia={isSDFEnergia}
						/>
					</div>
				)}
			</Modal>
		</>
	);
};

// Componente Comentario adaptado para móvil
const Comentario = ({ registro, isSDF, isSDFEnergia }) => {
	const containerStyle = {
		fontSize: "14px",
		lineHeight: "1.8",
	};

	const labelStyle = {
		fontWeight: "600",
		color: "#262626",
		display: "block",
		marginBottom: "4px",
		fontSize: "13px",
	};

	const valueStyle = {
		color: "#595959",
		display: "block",
		marginBottom: "12px",
		fontSize: "14px",
		backgroundColor: "#fafafa",
		padding: "8px",
		borderRadius: "4px",
	};

	const fieldStyle = {
		marginBottom: "12px",
	};

	return (
		<div style={containerStyle}>
			{(isSDF || isSDFEnergia) && (
				<>
					{registro.informacionAdicional ? (
						<div>
							{Object.entries(registro.informacionAdicional).map(([key, value], index) => (
								<div key={index} style={fieldStyle}>
									<span style={labelStyle}>
										{key.replace(/([A-Z])/g, " $1").trim().toUpperCase()}:
									</span>
									<span style={valueStyle}>
										{typeof value === "boolean"
											? value ? "Sí" : "No"
											: typeof value === 'string' &&
											  value.match(/^\d{4}-\d{2}-\d{2}T/) &&
											  moment(value).isValid()
											? moment(value).format("DD/MM/YYYY")
											: value || 'N/A'}
									</span>
								</div>
							))}
							{registro.comentario && (
								<div style={fieldStyle}>
									<span style={labelStyle}>COMENTARIO:</span>
									<span style={valueStyle}>{registro.comentario}</span>
								</div>
							)}
						</div>
					) : (
						registro.comentario && (
							<div style={fieldStyle}>
								<span style={labelStyle}>COMENTARIO:</span>
								<span style={valueStyle}>{registro.comentario}</span>
							</div>
						)
					)}
				</>
			)}
		</div>
	);
};

const Header = () => {
	const history = useHistory();
	const onClickBtnRetornar = () => {
		history.push("/vacaciones/solicitudes");
	};

	return (
		<div className="container-movil-pdf-header">
			<div style={{ width: "100%", display: "flex", alignItems: "center" }} onClick={onClickBtnRetornar}>
				<ArrowLeftOutlined style={{ fontSize: "20px", marginRight: "8px" }} />
				Solicitudes Pendientes de Aprobación
			</div>
		</div>
	);
};

export default SolicitudesPendientesAprobarPage;
